<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

/**
 * A horizontal rail that the browser scrolls.
 *
 * CSS scroll snapping does the work: it swipes on a phone, it scrolls with a
 * trackpad, the contents are real links a keyboard can tab through, and nothing
 * moves on its own. The arrows and dots are conveniences on top — remove the
 * script and the rail still scrolls.
 *
 * Shared by the tutor rail and the subject rail so the two behave identically.
 * The parent supplies the items through the default slot; this component owns
 * only the scrolling.
 */
defineProps({
  label: { type: String, default: 'Carousel' },
  /**
   * The items. Passed as a prop rather than read from the default slot: a
   * `v-for` in the parent's slot compiles to a single fragment vnode, so the
   * rail cannot see the individual children to wrap and snap them.
   */
  items: { type: Array, default: () => [] },
  /** Tailwind width classes for each item, per breakpoint. */
  itemClass: { type: String, default: 'w-[78%] sm:w-[46%] lg:w-[31%] xl:w-[23.5%]' },
  /** Optional key on each item; falls back to the index. */
  itemKey: { type: String, default: 'id' },
})

const rail = ref(null)
const atStart = ref(true)
const atEnd = ref(false)
const activePage = ref(0)
const pageCount = ref(1)

const measure = () => {
  const el = rail.value
  if (!el) return

  const max = el.scrollWidth - el.clientWidth
  atStart.value = el.scrollLeft <= 4
  atEnd.value = el.scrollLeft >= max - 4
  pageCount.value = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth))
  activePage.value = max <= 0 ? 0 : Math.round(el.scrollLeft / el.clientWidth)
}

const scrollByPage = (direction) => {
  const el = rail.value
  if (!el) return
  el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: 'smooth' })
}

const goToPage = (index) => {
  const el = rail.value
  if (!el) return
  el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' })
}

const hasOverflow = computed(() => pageCount.value > 1)

let observer

onMounted(() => {
  measure()
  observer = new ResizeObserver(measure)
  if (rail.value) observer.observe(rail.value)
})

onBeforeUnmount(() => observer?.disconnect())

defineExpose({ scrollByPage, atStart, atEnd })
</script>

<template>
  <div>
    <div v-if="$slots.controls || hasOverflow" class="mb-6 flex flex-wrap items-end justify-between gap-4">
      <slot name="heading" />
      <div class="flex items-center gap-3">
        <slot name="controls" />
        <div v-if="hasOverflow" class="hidden gap-2 md:flex">
          <button
            type="button"
            class="grid size-10 place-items-center rounded-full border border-line bg-surface text-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-35"
            :disabled="atStart"
            :aria-label="`Previous ${label}`"
            @click="scrollByPage(-1)"
          >
            <ChevronLeft class="size-5" />
          </button>
          <button
            type="button"
            class="grid size-10 place-items-center rounded-full border border-line bg-surface text-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-35"
            :disabled="atEnd"
            :aria-label="`More ${label}`"
            @click="scrollByPage(1)"
          >
            <ChevronRight class="size-5" />
          </button>
        </div>
      </div>
    </div>

    <!--
      The card at the edge used to be sliced off mid-word. It now fades out
      instead, which reads as "there is more" rather than as a rendering fault.
      The mask is applied on the container and removed at each end, so the first
      and last cards are never dimmed for no reason.
    -->
    <ul
      ref="rail"
      class="rail -mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-1 pb-3"
      :class="[!atStart && 'fade-start', !atEnd && 'fade-end']"
      :aria-label="label"
      @scroll.passive="measure"
    >
      <li
        v-for="(item, index) in items"
        :key="item?.[itemKey] ?? index"
        :class="[itemClass, 'shrink-0 snap-start']"
      >
        <slot name="item" :item="item" :index="index" />
      </li>
    </ul>

    <div v-if="hasOverflow" class="mt-4 flex justify-center gap-2 md:hidden">
      <button
        v-for="page in pageCount"
        :key="page"
        type="button"
        class="h-2 rounded-full transition-all duration-200"
        :class="activePage === page - 1 ? 'w-6 bg-brand' : 'w-2 bg-line'"
        :aria-label="`Go to ${label} ${page}`"
        :aria-current="activePage === page - 1"
        @click="goToPage(page - 1)"
      ></button>
    </div>
  </div>
</template>

<style scoped>
/* The rail scrolls; its scrollbar is noise next to the cards. */
.rail {
  scrollbar-width: none;
  /* Enough to soften the edge, not enough to dim a card you are reading. */
  --fade: 24px;
  transition: -webkit-mask-image 200ms var(--ease), mask-image 200ms var(--ease);
}
.rail::-webkit-scrollbar {
  display: none;
}

/* A soft edge instead of a hard cut. Both ends, either end, or neither —
   whichever the scroll position calls for. */
.rail.fade-start.fade-end {
  -webkit-mask-image: linear-gradient(to right, transparent, #000 var(--fade), #000 calc(100% - var(--fade)), transparent);
  mask-image: linear-gradient(to right, transparent, #000 var(--fade), #000 calc(100% - var(--fade)), transparent);
}
.rail.fade-end:not(.fade-start) {
  -webkit-mask-image: linear-gradient(to right, #000 calc(100% - var(--fade)), transparent);
  mask-image: linear-gradient(to right, #000 calc(100% - var(--fade)), transparent);
}
.rail.fade-start:not(.fade-end) {
  -webkit-mask-image: linear-gradient(to right, transparent, #000 var(--fade));
  mask-image: linear-gradient(to right, transparent, #000 var(--fade));
}
</style>
